import * as Yup from 'yup';
import { ALL_BUSINESS_CONTENT } from './BusinessContentRoute';
import { Button, FormTextField } from 'ui';
import { Form, Formik } from 'formik';
import { gql } from '@apollo/client';
import {
  useCreateBusinessContentMutation,
  useUpdateBusinessContentMutation,
} from 'generated/graphql';
import { useLocation, useNavigate, useParams } from 'react-router-v7';
import ClowncarBodyV2 from 'components/ClowncarBodyV2';
import ClowncarHeaderV2 from 'components/ClowncarHeaderV2';
import React from 'react';

gql`
  mutation CreateBusinessContent($businessContent: UpdateBusinessContentInput!) {
    createBusinessContent(businessContent: $businessContent) {
      venueId
      businessId
      needToKnow
      whyWeLikeIt
    }
  }
`;

gql`
  mutation UpdateBusinessContent($businessContent: UpdateBusinessContentInput!) {
    updateBusinessContent(businessContent: $businessContent) {
      venueId
      businessId
      needToKnow
      whyWeLikeIt
    }
  }
`;

const businessContentSchema = Yup.object({
  venueId: Yup.mixed(),
  businessId: Yup.mixed(),
  needToKnow: Yup.string().required('Name is required'),
  whyWeLikeIt: Yup.string().required('Description is required'),
});

export interface BusinessContentFormValues {
  venueId?: number | string | null;
  businessId?: number | string | null;
  needToKnow: string;
  whyWeLikeIt: string;
}

const emptyState: BusinessContentFormValues = {
  venueId: null,
  businessId: null,
  needToKnow: '',
  whyWeLikeIt: '',
};

const BusinessContentFormRoute: React.FC = () => {
  const { businessId = null, venueId = null } = useParams<{
    businessId?: string;
    venueId?: string;
  }>();
  const isEditing = !!businessId || !!venueId;
  const navigate = useNavigate();
  const location = useLocation();

  const [createBusinessContent] = useCreateBusinessContentMutation();
  const [updateBusinessContent] = useUpdateBusinessContentMutation();

  // Initial values come from router location state when editing (passed by the detail page)
  const initialValues: BusinessContentFormValues =
    isEditing && location.state ? (location.state as BusinessContentFormValues) : emptyState;

  const onSubmit = async (values: BusinessContentFormValues) => {
    if (isEditing) {
      await updateBusinessContent({
        variables: {
          businessContent: {
            venueId: venueId || values.venueId ? Number(venueId || values.venueId) : null,
            businessId:
              businessId || values.businessId ? Number(businessId || values.businessId) : null,
            needToKnow: values.needToKnow,
            whyWeLikeIt: values.whyWeLikeIt,
          },
        },
        refetchQueries: [{ query: ALL_BUSINESS_CONTENT }],
      });
    } else {
      await createBusinessContent({
        variables: {
          businessContent: {
            venueId: values.venueId ? Number(values.venueId) : null,
            businessId: values.businessId ? Number(values.businessId) : null,
            needToKnow: values.needToKnow,
            whyWeLikeIt: values.whyWeLikeIt,
          },
        },
        refetchQueries: [{ query: ALL_BUSINESS_CONTENT }],
      });
    }
    navigate('/jce-business-content');
  };

  return (
    <div className="Content">
      <ClowncarHeaderV2
        heading={
          isEditing ? `Edit Business Content #${businessId || venueId}` : 'Create Business Content'
        }
        actions={
          <Button variant="outlined" onClick={() => navigate('/jce-business-content')}>
            ← Back
          </Button>
        }
      />
      <ClowncarBodyV2>
        <Formik<BusinessContentFormValues>
          initialValues={initialValues}
          enableReinitialize
          validationSchema={businessContentSchema}
          onSubmit={onSubmit}
        >
          {({ values, dirty, isValid, isSubmitting }) => (
            <Form>
              <FormTextField
                fullWidth
                label="Venue Id"
                name="venueId"
                value={venueId || values.venueId}
              />
              <FormTextField
                fullWidth
                label="Business Id"
                name="businessId"
                value={businessId || values.businessId}
              />
              <FormTextField
                fullWidth
                label="Need to Know"
                name="needToKnow"
                value={values.needToKnow}
                multiline
                rows={3}
                required
              />
              <FormTextField
                fullWidth
                label="Why We Like It"
                name="whyWeLikeIt"
                value={values.whyWeLikeIt}
                multiline
                rows={3}
                required
              />
              <Button
                color="primary"
                disabled={!dirty || !isValid || isSubmitting}
                size="large"
                type="submit"
                variant="contained"
                style={{ marginTop: 16 }}
              >
                {isEditing ? 'Save Changes' : 'Create'}
              </Button>
            </Form>
          )}
        </Formik>
      </ClowncarBodyV2>
    </div>
  );
};

export default BusinessContentFormRoute;
